import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { get, pickBy } from 'lodash';

import { logImpression as logImpressionAction } from '@sparefoot/react-core/modules/experiments';

const INACTIVE_EXPERIMENT = 'inactive';

function getExperimentTreatment(experimentName, bucket, forcedBucket, defaultVariation = 'control') {
	const buckets = {
		...forcedBucket,
		...pickBy(bucket, (value) => value !== INACTIVE_EXPERIMENT),
	};
	const variation = get(buckets, experimentName) || INACTIVE_EXPERIMENT;
	return variation === INACTIVE_EXPERIMENT ? defaultVariation : variation;
}

function isExperimentActive(experimentName, bucket) {
	return (get(bucket, experimentName) || INACTIVE_EXPERIMENT) !== INACTIVE_EXPERIMENT;
}

/**
 * Hook replacement for withExperiment from @sparefoot/react-core/decorators.
 *
 * Usage:
 *   const { variation, variationKey } = useExperiment('sf_15342_geolocation_sfdc');
 *   // variation = 'control' | 'variation1' | 'variation2' | etc.
 *   // variationKey = 'sf15342Variation'
 *
 * Or to get the same prop name as the HOC:
 *   const sf15342Variation = useExperiment('sf_15342_geolocation_sfdc').variation;
 */
export default function useExperiment(experimentName, shouldLogImpression = true) {
	const dispatch = useDispatch();
	const bucket = useSelector((state) => state.experiments.bucket);
	const forcedBucket = useSelector((state) => state.experiments.forcedBucket);

	const bucketedVariation = useMemo(
		() => getExperimentTreatment(experimentName, bucket, forcedBucket),
		[experimentName, bucket, forcedBucket],
	);

	useEffect(() => {
		if (shouldLogImpression && isExperimentActive(experimentName, bucket)) {
			try {
				dispatch(logImpressionAction(experimentName));
			} catch (e) {
				// TrackingClient may not be initialized yet
				console.warn(
					'[useExperiment] Failed to log impression:',
					e.message,
				);
			}
		}
	}, [experimentName, bucket, shouldLogImpression, dispatch]);

	// Build the variation key the same way the HOC does:
	// sf_15342_geolocation_sfdc → sf15342Variation
	const pieces = experimentName.split('_');
	const variationKey = `${pieces[0]}${pieces[1]}Variation`;

	return {
		variation: bucketedVariation,
		variationKey,
		[variationKey]: bucketedVariation,
	};
}
